import { FC, useCallback, useEffect, useState } from 'react';
import {
  Stack,
  Text,
  Button,
  Card,
  Group,
  Badge,
  Divider,
  Box,
  Alert,
} from '@mantine/core';
import {
  IconCircleCheck,
  IconUser,
  IconCalendar,
  IconClipboardText,
  IconInfoCircle,
} from '@tabler/icons-react';
import { Encounter, Patient, Practitioner } from '@medplum/fhirtypes';
import { formatHumanName } from '@medplum/core';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useMedplum } from '@medplum/react';
import dayjs from 'dayjs';
import { HL7 } from '@/consts/systems.consts';

interface EncounterCreatedConfirmationProps {
  encounter: Encounter;
  patient: Patient;
  onOnboardAnother: () => void;
  isExistingEncounter?: boolean;
}

/**
 * Confirmation screen after successfully creating an encounter
 * Shows encounter details and provides option to onboard another patient
 * Fetches and displays attending and referring doctors from the encounter
 *
 * @param props - Component props
 * @param props.encounter - The created encounter
 * @param props.patient - The patient for the encounter
 * @param props.onOnboardAnother - Callback to start onboarding another patient
 * @param props.isExistingEncounter - The patient may already have an encounter in-progress
 * @returns JSX.Element
 */
export const EncounterCreatedConfirmation: FC<
  EncounterCreatedConfirmationProps
> = ({
  encounter,
  patient,
  onOnboardAnother,
  isExistingEncounter = false,
}) => {
  const { t } = useTranslation('patient-onboarding');
  const navigate = useNavigate();
  const medplum = useMedplum();
  const [attendingDoctor, setAttendingDoctor] = useState<Practitioner | null>(null);
  const [referringDoctor, setReferringDoctor] = useState<Practitioner | null>(null);

  // Fetch doctors from encounter participants
  useEffect(() => {
    const fetchDoctors = async () => {
      const participants = encounter.participant || [];

      const attendingParticipant = participants.find((p) =>
        p.type?.some((t) =>
          t.coding?.some(
            (c) => c.code === HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.ATND
          )
        )
      );

      const referringParticipant = participants.find((p) =>
        p.type?.some((t) =>
          t.coding?.some(
            (c) =>
              c.code === HL7.TERMINOLOGY.CS.V3.PARTICIPATION_TYPE.CODES.REFERRER
          )
        )
      );

      if (attendingParticipant?.individual?.reference) {
        try {
          const attending = await medplum.readReference(
            attendingParticipant.individual
          );
          if (attending.resourceType === 'Practitioner') {
            setAttendingDoctor(attending);
          }
        } catch (error) {
          console.error('Error fetching attending doctor:', error);
        }
      }

      if (referringParticipant?.individual?.reference) {
        try {
          const referring = await medplum.readReference(
            referringParticipant.individual
          );
          if (referring.resourceType === 'Practitioner') {
            setReferringDoctor(referring);
          }
        } catch (error) {
          console.error('Error fetching referring doctor:', error);
        }
      }
    };

    fetchDoctors();
  }, [encounter, medplum]);

  const patientName = formatHumanName(patient.name?.[0]) || 'Unknown Patient';
  const encounterTypeName =
    encounter.type?.[0]?.coding?.[0]?.display ||
    encounter.type?.[0]?.text ||
    'Unknown Type';
  const attendingDoctorName = attendingDoctor
    ? formatHumanName(attendingDoctor.name?.[0])
    : 'Not specified';
  const referringDoctorName = referringDoctor
    ? formatHumanName(referringDoctor.name?.[0])
    : null;
  const reasonForVisit = encounter.reasonCode?.[0]?.text || 'Not specified';
  const createdAt = encounter.period?.start
    ? dayjs(encounter.period.start).format('DD/MM/YYYY HH:mm')
    : 'Unknown';
  const handleViewPatient = useCallback(() => {
    if (!patient.id) {
      return;
    }

    navigate(`/Patient/${patient.id}`);
  }, [patient.id, navigate]);

  return (
    <Stack gap="lg" align="center">
      {/* Success Icon or Info alert */}
      {isExistingEncounter ? (
        <Alert
          icon={<IconInfoCircle size={24} />}
          title={t('confirmation.existingEncounterTitle')}
          color="orange"
          variant="light"
          w="100%"
          maw={600}
        >
          {t('confirmation.existingEncounterMessage')}
        </Alert>
      ) : (
        <Stack align="center" gap="xs">
          <IconCircleCheck size={80} color="green" />
          <Text size="xl" fw={700} mt="xs">
            {t('confirmation.title')}
          </Text>
          <Text size="sm" c="dimmed">
            {t('confirmation.successMessage', { patientName })}
          </Text>
        </Stack>
      )}

      {/* Encounter Details Card */}
      <Card withBorder w="100%" maw={600} p="lg">
        <Text size="lg" fw={600} mb="md">
          {t('confirmation.encounterDetails')}
        </Text>

        <Stack gap="sm">
          {/* Patient */}
          <Group gap="sm">
            <IconUser size={18} />
            <Box flex={1}>
              <Text size="xs" c="dimmed">
                {t('encounter.patientLabel')}
              </Text>
              <Text size="sm" fw={500}>
                {patientName}
              </Text>
            </Box>
          </Group>

          <Divider />

          {/* Encounter Type */}
          <Group gap="sm">
            <IconClipboardText size={18} />
            <Box flex={1}>
              <Text size="xs" c="dimmed">
                {t('encounter.encounterTypeLabel')}
              </Text>
              <Badge variant="light" size="md" mt={4}>
                {encounterTypeName}
              </Badge>
            </Box>
          </Group>

          <Divider />

          {/* Attending Doctor */}
          <Group gap="sm">
            <IconUser size={18} />
            <Box flex={1}>
              <Text size="xs" c="dimmed">
                {t('encounter.attendingDoctorLabel')}
              </Text>
              <Text size="sm" fw={500}>
                {attendingDoctorName}
              </Text>
            </Box>
          </Group>

          {/* Referring Doctor */}
          {referringDoctorName && (
            <>
              <Divider />
              <Group gap="sm">
                <IconUser size={18} />
                <Box flex={1}>
                  <Text size="xs" c="dimmed">
                    {t('encounter.referringDoctorLabel')}
                  </Text>
                  <Text size="sm" fw={500}>
                    {referringDoctorName}
                  </Text>
                </Box>
              </Group>
            </>
          )}

          <Divider />

          {/* Reason for Visit */}
          <Group gap="sm" align="flex-start">
            <IconClipboardText size={18} style={{ marginTop: 2 }} />
            <Box flex={1}>
              <Text size="xs" c="dimmed">
                {t('encounter.reasonForVisitLabel')}
              </Text>
              <Text size="sm">{reasonForVisit}</Text>
            </Box>
          </Group>

          <Divider />

          {/* Created At */}
          <Group gap="sm">
            <IconCalendar size={18} />
            <Box flex={1}>
              <Text size="xs" c="dimmed">
                {t('confirmation.createdAt', { time: '' }).split(' {{')[0]}
              </Text>
              <Text size="sm" fw={500}>
                {createdAt}
              </Text>
            </Box>
          </Group>
        </Stack>
      </Card>

      {/* Actions */}
      <Group gap="md" mt="md">
        <Button size="lg" onClick={onOnboardAnother}>
          {t('confirmation.onboardAnotherButton')}
        </Button>
        <Button size="lg" onClick={handleViewPatient} disabled={!patient.id}>
          {t('confirmation.viewPatientButton')}
        </Button>
      </Group>
    </Stack>
  );
};
